/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Tooltip,
    Chip,
} from "@mui/material";
import {
    AddCircleOutline,
    EditOutlined,
    DeleteOutline,
    DescriptionOutlined,
    Email,
    Sms,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectTemplates,
    selectIsLoadingTemplates,
    selectTemplatesError,
    selectIsSavingTemplate,
    selectTemplateSaveError,
} from "../../redux/selectors/collectSelector";
import {
    IReminderTemplate,
    TemplateCategory,
    TemplateChannel,
} from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectTemplates — manage reminder templates (subject + body) with a
// live Handlebars-style preview that substitutes a small sample payload.
// ─────────────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
    upcoming: "Upcoming due",
    due_today: "Due today",
    overdue: "Overdue",
    payment_received: "Payment received",
    generic: "Generic",
};

const SAMPLE_VARS = {
    customerName: "Acme Corp",
    invoiceNumber: "INV-1027",
    amount: "$1,250.00",
    dueDate: "Jun 14, 2026",
    daysOverdue: "5",
    businessName: "Your Business",
    paymentLink: "https://pay.example.com/inv-1027",
};

const AVAILABLE_VARS = Object.keys(SAMPLE_VARS);

function renderPreview(template: string): string {
    // Minimal Handlebars-style {{var}} substitution for the preview pane.
    // The real rendering is server-side; this is just a UX aid.
    if (!template) return "";
    return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key) => {
        return (SAMPLE_VARS as any)[key] ?? m;
    });
}

function emptyTemplate(): IReminderTemplate {
    return {
        _id: "",
        businessId: "",
        name: "",
        channel: "email",
        category: "overdue",
        subject: "",
        body: "",
        isSystem: false,
        isActive: true,
    };
}

const CollectTemplates: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);
    const templates = useSelector(selectTemplates);
    const isLoading = useSelector(selectIsLoadingTemplates);
    const error = useSelector(selectTemplatesError);
    const isSaving = useSelector(selectIsSavingTemplate);
    const saveError = useSelector(selectTemplateSaveError);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState<IReminderTemplate>(emptyTemplate());

    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchTemplates({}));
    }, [dispatch, businessId]);

    const openCreate = () => {
        setDraft(emptyTemplate());
        setDialogOpen(true);
    };
    const openEdit = (t: IReminderTemplate) => {
        setDraft(JSON.parse(JSON.stringify(t)));
        setDialogOpen(true);
    };

    const onSave = async () => {
        if (!draft.name.trim() || !draft.body.trim()) return;

        // System templates are read-only on the server except for `isActive`.
        // Restrict the update payload accordingly to avoid the 400 error.
        if (draft._id && draft.isSystem) {
            const result = await dispatch(
                updateTemplate({
                    templateId: draft._id,
                    isActive: draft.isActive,
                }) as any,
            );
            if ((result as any).meta?.requestStatus === "fulfilled") {
                setDialogOpen(false);
                toast.success(
                    draft.isActive
                        ? "System template activated."
                        : "System template paused.",
                );
            } else {
                toast.error(
                    (result as any).payload ||
                    "Couldn't update template. Try again.",
                );
            }
            return;
        }

        const payload = {
            name: draft.name.trim(),
            channel: draft.channel,
            category: draft.category,
            subject: draft.subject.trim(),
            body: draft.body,
            isActive: draft.isActive,
        };
        const action = draft._id
            ? updateTemplate({ templateId: draft._id, ...payload })
            : createTemplate(payload);
        const result = await dispatch(action as any);
        if ((result as any).meta?.requestStatus === "fulfilled") {
            setDialogOpen(false);
            toast.success(draft._id ? "Template updated." : "Template created.");
        } else {
            toast.error(
                (result as any).payload || "Couldn't save template. Try again.",
            );
        }
    };

    // Create a writable copy of a system template so the user can customise
    // name/subject/body without touching the seeded version.
    const onCustomise = async () => {
        if (!draft.isSystem) return;
        const cloneName = `${draft.name} (custom)`;
        const result = await dispatch(
            createTemplate({
                name: cloneName,
                channel: draft.channel,
                category: draft.category,
                subject: draft.subject,
                body: draft.body,
                isActive: draft.isActive,
            }) as any,
        );
        if ((result as any).meta?.requestStatus === "fulfilled") {
            const created = (result as any).payload as IReminderTemplate;
            // Swap the dialog over to editing the new (writable) clone.
            if (created && created._id) {
                setDraft({ ...created });
                toast.success(
                    "Created an editable copy. You can now customise it.",
                );
            } else {
                setDialogOpen(false);
                toast.success("Created an editable copy.");
            }
        } else {
            toast.error(
                (result as any).payload || "Couldn't create a copy. Try again.",
            );
        }
    };

    const onDelete = async (t: IReminderTemplate) => {
        if (t.isSystem) {
            window.alert("System templates can't be deleted.");
            return;
        }
        if (!window.confirm(`Delete "${t.name}"?`)) return;
        const result: any = await dispatch(deleteTemplate({ templateId: t._id }));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Template deleted.");
        } else {
            toast.error("Couldn't delete template.");
        }
    };

    const previewSubject = useMemo(
        () => renderPreview(draft.subject),
        [draft.subject],
    );
    const previewBody = useMemo(() => renderPreview(draft.body), [draft.body]);

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 8 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Reminder templates
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                                maxWidth: 720,
                            }}
                        >
                            The actual emails and SMS we send. Use{" "}
                            <code>{`{{customerName}}`}</code> style placeholders to pull
                            in invoice data automatically.
                        </Typography>
                    </Box>
                    <CollectPrimaryButton
                        startIcon={<AddCircleOutline fontSize="small" />}
                        onClick={openCreate}
                        disabled={!businessId}
                    >
                        New template
                    </CollectPrimaryButton>
                </FlexBetween>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                {isLoading && templates.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress sx={{ color: colors.mainSecondary }} />
                    </Box>
                ) : templates.length === 0 ? (
                    <CollectSectionCard>
                        <CollectEmptyState>
                            <DescriptionOutlined
                                sx={{ fontSize: 36, color: "#9ca3af", mb: 1 }}
                            />
                            <CollectEmptyTitle>No templates yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Create your first reminder template. Campaigns reference
                                templates by name to know what to send.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    </CollectSectionCard>
                ) : (
                    <Grid container spacing={2}>
                        {templates.map((t) => (
                            <Grid item xs={12} md={6} key={t._id}>
                                <CollectSectionCard style={{ marginBottom: 0 }}>
                                    <FlexBetween>
                                        <Box sx={{ flex: 1 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        color: colors.primary,
                                                        fontSize: "1rem",
                                                    }}
                                                >
                                                    {t.name}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    icon={
                                                        t.channel === "email" ? (
                                                            <Email sx={{ fontSize: 14 }} />
                                                        ) : (
                                                            <Sms sx={{ fontSize: 14 }} />
                                                        )
                                                    }
                                                    label={t.channel.toLowerCase()}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={CATEGORY_LABELS[t.category]}
                                                    sx={{
                                                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                                                        color: "#1e40af",
                                                    }}
                                                />
                                                {t.isSystem && (
                                                    <Chip
                                                        size="small"
                                                        label="system"
                                                        sx={{
                                                            backgroundColor: "rgba(107,114,128,0.15)",
                                                            color: "#374151",
                                                        }}
                                                    />
                                                )}
                                                {!t.isActive && (
                                                    <Chip
                                                        size="small"
                                                        label="paused"
                                                        sx={{
                                                            backgroundColor: "rgba(245,158,11,0.18)",
                                                            color: "#92400e",
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {t.subject && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontSize: "0.85rem",
                                                        color: "#374151",
                                                        mt: 1,
                                                    }}
                                                >
                                                    <strong>Subject:</strong> {t.subject}
                                                </Typography>
                                            )}
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.82rem",
                                                    color: "#6b7280",
                                                    mt: 0.5,
                                                    whiteSpace: "pre-wrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: "vertical",
                                                }}
                                            >
                                                {t.body}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openEdit(t)}
                                                >
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDelete(t)}
                                                    disabled={t.isSystem || isSaving}
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </FlexBetween>
                                </CollectSectionCard>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Editor dialog with live preview */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                >
                    {draft._id
                        ? draft.isSystem
                            ? "System template (read-only)"
                            : "Edit template"
                        : "New template"}
                </DialogTitle>
                <DialogContent>
                    {draft.isSystem && (
                        <CollectInfoBanner tone="info">
                            <span>
                                System templates are seeded by FundsyFi and can't be
                                edited directly. You can pause or activate them here,
                                or click <strong>Customise</strong> to create your own
                                editable copy.
                            </span>
                        </CollectInfoBanner>
                    )}
                    {saveError && (
                        <CollectInfoBanner tone="warn">
                            <span>{saveError}</span>
                        </CollectInfoBanner>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                margin="normal"
                                value={draft.name}
                                onChange={(e) =>
                                    setDraft({ ...draft, name: e.target.value })
                                }
                                InputProps={{ readOnly: draft.isSystem }}
                                disabled={draft.isSystem}
                            />
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Channel"
                                    margin="normal"
                                    value={draft.channel}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            channel: e.target.value as TemplateChannel,
                                        })
                                    }
                                    disabled={draft.isSystem}
                                >
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="sms">SMS</MenuItem>
                                </TextField>
                                <TextField
                                    select
                                    fullWidth
                                    label="Category"
                                    margin="normal"
                                    value={draft.category}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            category: e.target.value as TemplateCategory,
                                        })
                                    }
                                    disabled={draft.isSystem}
                                >
                                    {(
                                        Object.keys(CATEGORY_LABELS) as TemplateCategory[]
                                    ).map((k) => (
                                        <MenuItem key={k} value={k}>
                                            {CATEGORY_LABELS[k]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            {draft.channel === "email" && (
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    margin="normal"
                                    value={draft.subject}
                                    onChange={(e) =>
                                        setDraft({ ...draft, subject: e.target.value })
                                    }
                                    InputProps={{ readOnly: draft.isSystem }}
                                    disabled={draft.isSystem}
                                />
                            )}
                            <TextField
                                fullWidth
                                label="Body"
                                margin="normal"
                                multiline
                                minRows={10}
                                value={draft.body}
                                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                                helperText="Supports {{customerName}}, {{invoiceNumber}}, {{amount}}, {{dueDate}}, {{daysOverdue}}, {{businessName}}, {{paymentLink}}."
                                InputProps={{ readOnly: draft.isSystem }}
                                disabled={draft.isSystem}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <Switch
                                    checked={draft.isActive}
                                    onChange={(e) =>
                                        setDraft({ ...draft, isActive: e.target.checked })
                                    }
                                />
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.85rem",
                                    }}
                                >
                                    Active
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    color: colors.primary,
                                    fontSize: "0.85rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    mt: 2,
                                }}
                            >
                                Live preview
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1,
                                    border: "1px solid rgba(15,23,42,0.08)",
                                    borderRadius: "10px",
                                    backgroundColor: "#fafafa",
                                    p: 2,
                                    minHeight: 320,
                                }}
                            >
                                {draft.channel === "email" && previewSubject && (
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            color: colors.primary,
                                            mb: 1,
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        {previewSubject}
                                    </Typography>
                                )}
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        whiteSpace: "pre-wrap",
                                        color: "#1f2937",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    {previewBody || (
                                        <span style={{ color: "#9ca3af" }}>
                                            Start typing your message body to see the preview…
                                        </span>
                                    )}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    color: colors.primary,
                                    fontSize: "0.75rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    mt: 2,
                                }}
                            >
                                Available variables
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 1 }}>
                                {AVAILABLE_VARS.map((v) => (
                                    <Chip
                                        key={v}
                                        size="small"
                                        label={`{{${v}}}`}
                                        onClick={() => {
                                            if (draft.isSystem) return;
                                            setDraft((d) => ({
                                                ...d,
                                                body: d.body + ` {{${v}}}`,
                                            }));
                                        }}
                                        sx={{
                                            fontFamily: "monospace",
                                            cursor: draft.isSystem
                                                ? "not-allowed"
                                                : "pointer",
                                            opacity: draft.isSystem ? 0.5 : 1,
                                            backgroundColor: "rgba(30,193,149,0.1)",
                                            color: "#0f7a5b",
                                        }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setDialogOpen(false)}>
                        Cancel
                    </CollectGhostButton>
                    {draft._id && draft.isSystem && (
                        <CollectGhostButton
                            onClick={onCustomise}
                            disabled={isSaving}
                        >
                            Customise (create copy)
                        </CollectGhostButton>
                    )}
                    <CollectPrimaryButton
                        onClick={onSave}
                        disabled={
                            isSaving || !draft.name.trim() || !draft.body.trim()
                        }
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : undefined
                        }
                    >
                        {isSaving
                            ? "Saving…"
                            : draft._id && draft.isSystem
                                ? draft.isActive
                                    ? "Save (pause/activate only)"
                                    : "Save (pause/activate only)"
                                : "Save template"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectTemplates;
